import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAsidePanelResize } from "@/hooks/use-aside-view";
import { useIsMobile, useMediaQuery } from "@/hooks/use-media-query";
import type { Task } from "@/lib/project-types";
import { cn } from "@/lib/utils";

export interface KanbanSplitViewProps {
  activeTask: Task | null;
  /** The same board is rendered in a constrained left pane while chat is open. */
  kanban: ReactNode;
  /** Normally the existing task ChatInterface, which owns ChatPromptComposer. */
  chat: ReactNode;
  /** Close the task chat — on mobile this dismisses the full-screen overlay. */
  onClose: () => void;
  className?: string;
}

/** A responsive board/chat layout that keeps the board mounted across selection. */
export function KanbanSplitView({
  activeTask,
  kanban,
  chat,
  onClose,
  className,
}: KanbanSplitViewProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isSideBySide = useMediaQuery("(min-width: 1024px)");
  // A task split can coexist with a URL-backed thread aside, so it owns an
  // independent controller rather than consuming the aside manager's resize.
  const resize = useAsidePanelResize("kanban-split");
  const hasTask = Boolean(activeTask);

  // Keep the board in the page while a mobile task chat opens in a dialog.
  if (isMobile) {
    return (
      <div className={cn("flex min-w-0 flex-1 flex-col", className)}>
        <div className="mx-auto flex w-full max-w-[96rem] flex-1">{kanban}</div>
        {hasTask && (
          <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent
              className="h-full max-h-full w-full max-w-full rounded-none p-0 gap-0 flex flex-col overflow-hidden"
              hideCloseButton
              data-testid="kanban-split-view"
            >
              <DialogTitle className="sr-only">{activeTask?.title}</DialogTitle>
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">{chat}</div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  // ── Desktop: side-by-side split ──────────────────────────────────────────
  return (
    <div
      className={cn("flex min-w-0 flex-1 flex-col", hasTask && "lg:flex-row", className)}
      data-testid={hasTask ? "kanban-split-view" : undefined}
    >
      <aside
        className={cn(
          "relative flex min-w-0 flex-col",
          hasTask
            ? "w-full flex-[0_0_auto] border-b bg-muted/10 lg:w-auto lg:border-b-0 lg:border-r"
            : "mx-auto w-full max-w-[96rem] flex-1",
        )}
        style={{ width: hasTask && isSideBySide ? resize.width : undefined }}
      >
        {hasTask && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={t("viewer.dragToResize")}
            onPointerDown={resize.onPointerDown}
            onPointerMove={resize.onPointerMove}
            onPointerUp={resize.onPointerUp}
            onDoubleClick={resize.onDoubleClick}
            className="absolute -right-[3px] top-0 z-20 hidden h-full w-1.5 cursor-col-resize touch-none select-none transition-colors hover:bg-primary/30 active:bg-primary/40 lg:block"
            data-testid="kanban-split-resize-handle"
          />
        )}
        <div className="flex flex-1 flex-col">{kanban}</div>
      </aside>
      {hasTask && (
        <section className="flex h-[calc(100dvh-53px)] min-h-[24rem] min-w-0 flex-1 flex-col bg-background lg:sticky lg:top-0 lg:self-start">{chat}</section>
      )}
    </div>
  );
}
